import { CATEGORY_TITLES } from '../../utils/resumeFormat';
import suitupLogo from '../../assets/suitup-logo.png';

// Live, instant HTML mirror of the generated PDF ("gold-rail" template). It shows
// the exact same structured content and spec ordering (§6) so the applicant sees
// the real resume take shape without a server round-trip. The downloadable file
// is a true PDF rendered server-side from this same model; the server template
// (applicant/v1/applicants/resume.html.erb) mirrors this layout.

const GOLD = '#9a5b16';   // headings + name (dark gold)
const RAIL = '#c8a24a';   // rails + bullet markers (light gold)
// The footer shows the SuitUp office number (not the applicant's own phone).
const OFFICE_PHONE = '03-7767762';

const linkedinHref = (value) => {
  const link = String(value || '').trim();
  if (!link) return null;
  return /^https?:\/\//i.test(link) ? link : `https://${link}`;
};

// Gold section heading (no separator rail).
const Heading = ({ children }) => (
  <h2 className="mt-3 mb-1 text-[13px] font-bold" style={{ color: GOLD }}>{children}</h2>
);

// "years \t details" info line: years pinned right, details flowing after.
// Fixed-width lead so every section's detail column lands on the same tab stop
// (matches td.lead in resume.html.erb).
const TabLine = ({ leading, children }) => (
  <p className="flex gap-3 text-[11px] leading-5 text-[#262626]">
    <span className="w-[74px] shrink-0 font-bold" style={{ color: GOLD }}>{leading}</span>
    <span>{children}</span>
  </p>
);

const ResumePreview = ({ model, identity }) => {
  const fullName = String(identity?.fullName || '').trim();
  const phone = String(identity?.phone || '').trim();
  const email = String(identity?.email || '').trim();
  const linkedin = String(identity?.linkedin || '').trim();
  const contactParts = [phone, email].filter(Boolean);
  const hasContact = contactParts.length > 0 || Boolean(linkedin);

  return (
    <div dir="rtl" className="min-h-[420px] bg-white px-8 py-6 text-right font-sans text-[#262626]">
      {/* Branded header: name + contact on the right (start), suitUp logo on the left (end). */}
      <div className="flex items-start justify-between gap-4">
        <div>
          {fullName && <h1 className="text-[20px] font-bold" style={{ color: GOLD }}>{fullName}</h1>}
          {hasContact && (
            <p className="mt-1 text-[11px] text-gray-600">
              {contactParts.join(' · ')}
              {contactParts.length > 0 && linkedin ? ' · ' : ''}
              {linkedin && (
                <a href={linkedinHref(linkedin)} target="_blank" rel="noreferrer" style={{ color: GOLD }}>{linkedin}</a>
              )}
            </p>
          )}
        </div>
        <img src={suitupLogo} alt="SuitUp" className="h-8 w-auto shrink-0" />
      </div>

      {/* Double gold rail under the header. */}
      <div className="mt-3 mb-1">
        <div className="h-px" style={{ backgroundColor: RAIL }} />
        <div className="mt-0.5 h-px" style={{ backgroundColor: RAIL }} />
      </div>

      {model.summary && (
        <section>
          <p className="mt-2 text-[11px] leading-5 text-justify">{model.summary}</p>
        </section>
      )}

      {model.experience.length > 0 && (
        <section>
          <Heading>{CATEGORY_TITLES.experience}</Heading>
          {model.experience.map((entry, index) => (
            <div key={index} className="mb-1.5">
              <p className="flex gap-3 text-[11px] leading-5">
                <span className="w-[74px] shrink-0 font-bold" style={{ color: GOLD }}>{entry.period}</span>
                <span>
                  <strong>{entry.company}</strong>
                  {entry.company && entry.role ? ' | ' : ''}
                  {entry.role}
                </span>
              </p>
              {entry.bullets.map((bullet, bulletIndex) => (
                <p key={bulletIndex} className="flex gap-1 pr-[74px] text-[11px] leading-5">
                  <span aria-hidden="true" style={{ color: RAIL }}>•</span>
                  <span>{bullet}</span>
                </p>
              ))}
            </div>
          ))}
        </section>
      )}

      {model.education.length > 0 && (
        <section>
          <Heading>{CATEGORY_TITLES.education}</Heading>
          {model.education.map((entry, index) => (
            <TabLine key={index} leading={entry.years}>
              <strong>{entry.institution}</strong>
              {entry.institution && entry.details ? ' | ' : ''}
              {entry.details}
            </TabLine>
          ))}
        </section>
      )}

      {model.relevantSkills.length > 0 && (
        <section>
          <Heading>{CATEGORY_TITLES.relevantSkills}</Heading>
          {model.relevantSkills.map((entry, index) => (
            <p key={index} className="mb-1 text-[11px] leading-5">
              <strong>{entry.title}</strong>: {entry.description}
            </p>
          ))}
        </section>
      )}

      {model.militaryService.length > 0 && (
        <section>
          <Heading>{CATEGORY_TITLES.militaryService}</Heading>
          {model.militaryService.map((entry, index) => (
            <TabLine key={index} leading={entry.years}>{entry.detail}</TabLine>
          ))}
        </section>
      )}

      {model.languages && (
        <section>
          <Heading>{CATEGORY_TITLES.languages}</Heading>
          <p className="text-[11px] leading-5">{model.languages}</p>
        </section>
      )}

      {model.computerSkills && (
        <section>
          <Heading>{CATEGORY_TITLES.computerSkills}</Heading>
          <p className="text-[11px] leading-5">{model.computerSkills}</p>
        </section>
      )}

      {model.courses.length > 0 && (
        <section>
          <Heading>{CATEGORY_TITLES.courses}</Heading>
          {model.courses.map((entry, index) => (
            <TabLine key={index} leading={entry.year}>{entry.name}</TabLine>
          ))}
        </section>
      )}

      {/* Footer: gold rail, SuitUp office phone on the left (end), logo on the right (start). */}
      <div className="mt-6">
        <div className="h-px" style={{ backgroundColor: RAIL }} />
        <div className="mt-1.5 flex items-center justify-between">
          <img src={suitupLogo} alt="SuitUp" className="h-5 w-auto" />
          <span className="text-[10px] font-bold text-gray-500">{OFFICE_PHONE}</span>
        </div>
      </div>
    </div>
  );
};

export default ResumePreview;
